// src/components/tools/management/MeetingTool.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowLeft, Plus, Search, Edit2, Trash2, Eye, Calendar, Clock, MapPin, ChevronRight, ChevronDown,
  Users, FileText, CheckSquare, Briefcase, Play, Save, Settings, Layers, Star, X, Target,
  MessageSquare, LayoutTemplate, Download, SortDesc, SortAsc, ListTodo, PlusCircle, LinkIcon,
  Loader2, RefreshCw, List, PlusCircle as NewMeetingIcon, CheckCircle, Mic, StopCircle, Wand2,
} from 'lucide-react';
import { firestoreService } from '../../../services/firestoreService';
import { getFunctions, httpsCallable } from 'firebase/functions'; 
import { VoiceRecorder } from '../../shared/VoiceRecorder'; 
import { VoicePlayer } from '../../shared/VoicePlayer';

// ============================================================
// CONSTANTS & ENUMS (Extracted from MeetingListView/Editor)
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  LIST: 'LIST',
  EDITOR: 'EDITOR',
  VIEW: 'VIEW'
};

const MeetingStatus = {
  PLANNED: 'PLANNED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED'
};

const MinuteType = {
  NOTE: 'NOTE',
  DECISION: 'DECISION',
  TASK: 'TASK'
};

// ============================================================
// UI HELPER COMPONENTS (Adapted for MeetingTool)
// ============================================================

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const Badge = ({ children, color }) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
        {children}
    </span>
);

const getStatusStyles = (status) => {
    const styles = {
        [MeetingStatus.ACTIVE]: { color: 'green', ring: 'ring-green-500' },
        [MeetingStatus.COMPLETED]: { color: 'gray', ring: 'ring-gray-300' },
        [MeetingStatus.PLANNED]: { color: 'indigo', ring: 'ring-indigo-500' },
    };
    return styles[status] || styles[MeetingStatus.PLANNED];
};

const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ============================================================
// MEETING LIST VIEW
// ============================================================

const MeetingsList = ({ meetings, onSelectMeeting, onCreateMeeting, customStyling }) => {
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');
    
    const filteredMeetings = useMemo(() => {
        return meetings
          .filter(m => {
            const matchesStatus = filterStatus === 'ALL' || m.status === filterStatus;
            const query = searchQuery.toLowerCase();
            const matchesSearch = 
              m.title?.toLowerCase().includes(query) ||
              (m.location || '').toLowerCase().includes(query) ||
              (m.date || '').includes(query);
            return matchesStatus && matchesSearch;
          })
          .sort((a, b) => {
            const dateA = new Date(b.date).getTime();
            const dateB = new Date(a.date).getTime();
            return sortOrder === 'desc' ? dateA - dateB : dateB - dateA;
          });
    }, [meetings, filterStatus, searchQuery, sortOrder]);

    const MeetingCard = ({ meeting }) => {
        const statusConfig = getStatusStyles(meeting.status);
        const openTaskCount = (meeting.minutes || []).filter(m => m.type === MinuteType.TASK && !m.completed).length;

        return (
            <div 
                className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-lg transition-all cursor-pointer group relative`}
                onClick={() => onSelectMeeting(meeting)}
            >
                <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                        <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 border-2 border-${statusConfig.color}-200 bg-${statusConfig.color}-50 text-${statusConfig.color}-700`}>
                            <span className="text-[10px] font-bold uppercase tracking-wide">
                                {new Date(meeting.date).toLocaleString('default', { month: 'short' })}
                            </span>
                            <span className="text-xl font-bold leading-none">
                                {new Date(meeting.date).getDate()}
                            </span>
                        </div>

                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors flex items-center gap-2 flex-wrap">
                                <span className="truncate">{meeting.title}</span>
                                <Badge color={statusConfig.color}>{meeting.status}</Badge>
                            </h3>
                            
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                                <span className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" /> {meeting.time} 
                                </span>
                                {meeting.location && (
                                    <span className="flex items-center truncate max-w-[200px]">
                                        <MapPin className="w-4 h-4 mr-1 shrink-0" /> {meeting.location}
                                    </span>
                                )}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-3 text-xs">
                                <span className="flex items-center px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                    <Users className="w-3 h-3 mr-1" /> {(meeting.participants || []).length} Participants
                                </span>
                                {openTaskCount > 0 && (
                                    <span className="flex items-center px-2 py-1 bg-amber-100 rounded-full text-amber-700 font-medium">
                                        <CheckSquare className="w-3 h-3 mr-1" /> {openTaskCount} Open Tasks
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 transition-colors" />
                </div>
            </div>
        );
    };

    if (filteredMeetings.length === 0 && meetings.length > 0) {
        return <EmptyState icon={Search} title="No matches found" description="Try adjusting your search or filters" action={<button onClick={() => { setSearchQuery(''); setFilterStatus('ALL'); }} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">Clear filters</button>} />;
    }
    
    if (meetings.length === 0) {
        return <EmptyState icon={Briefcase} title="No meetings yet" description="Create your first meeting to get started" action={<button onClick={onCreateMeeting} className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"><Plus className="w-5 h-5" /> Create Meeting</button>} />;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input 
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
                      placeholder="Search by title, location, or date..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button
                    onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                    className="flex items-center px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    {sortOrder === 'desc' ? (
                        <><SortDesc className="w-4 h-4 mr-1" /> Newest</>
                    ) : (
                        <><SortAsc className="w-4 h-4 mr-1" /> Oldest</>
                    )}
                </button>
            </div>
            
            <div className="space-y-4">
                {filteredMeetings.map(meeting => (
                    <MeetingCard key={meeting.id} meeting={meeting} />
                ))}
            </div>
        </div>
    );
};

// ============================================================
// MEETING EDITOR (Full Feature Restoration)
// ============================================================

const MeetingEditor = ({ meeting: initialMeeting, onSave, onBack, saving, customStyling, toolsService, bookId, firebaseApp }) => {
    const [meeting, setMeeting] = useState(initialMeeting);
    const [activeTab, setActiveTab] = useState(initialMeeting.status === MeetingStatus.ACTIVE ? 'run' : initialMeeting.status === MeetingStatus.COMPLETED ? 'review' : 'plan');
    const [newParticipant, setNewParticipant] = useState('');
    const [newObjective, setNewObjective] = useState(''); 
    const [expandedPlanAgendaId, setExpandedPlanAgendaId] = useState(null); 
    const [newResourceLink, setNewResourceLink] = useState(''); 
    const [newResourceTitle, setNewResourceTitle] = useState(''); 
    
    // Minutes State
    const [minuteInput, setMinuteInput] = useState('');
    const [minuteType, setMinuteType] = useState(MinuteType.NOTE);
    const [taskOwner, setTaskOwner] = useState(''); 
    const [taskDeadline, setTaskDeadline] = useState(''); 
    const [selectedAgendaItem, setSelectedAgendaItem] = useState('');

    const [isUploading, setIsUploading] = useState(false);

    const functions = getFunctions(firebaseApp);
    const transcribeAudioCF = httpsCallable(functions, 'transcribeAudio');

    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);

    const [isRecorderOpen, setIsRecorderOpen] = useState(false);
    const [currentVoiceNote, setCurrentVoiceNote] = useState(null); 
    const [isTranscribing, setIsTranscribing] = useState(false);

    const mediaRecorderRef = useRef(null);
    const timerRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleUpdate = (field, value) => {
        setMeeting(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        onSave(meeting);
    };

    // --- AGENDA & OBJECTIVES LOGIC (Restored) ---
    const addObjective = () => {
        if (!newObjective.trim()) return;
        const currentObjectives = meeting.objectives || [];
        handleUpdate('objectives', [...currentObjectives, newObjective]);
        setNewObjective('');
    };

    const removeObjective = (index) => {
        const currentObjectives = meeting.objectives || [];
        handleUpdate('objectives', currentObjectives.filter((_, i) => i !== index));
    };

    const addAgendaItem = () => {
        const newItem = {
            id: Date.now(),
            title: 'New Agenda Item',
            durationMinutes: 30, // <-- CORRECTED DEFAULT DURATION
            presenter: '',
            completed: false,
            notes: '',
            decisions: '',
            resources: []
        };
        handleUpdate('agenda', [...(meeting.agenda || []), newItem]);
    };

    const updateAgendaItem = (id, field, value) => {
        const newAgenda = (meeting.agenda || []).map(item => 
            item.id === id ? { ...item, [field]: value } : item
        );
        handleUpdate('agenda', newAgenda);
    };
    
    const togglePlanAgendaExpand = (id) => {
        setExpandedPlanAgendaId(prev => prev === id ? null : id);
        setNewResourceLink('');
        setNewResourceTitle('');
    };

    const addResource = (agendaId, type, title, url) => {
        const item = (meeting.agenda || []).find(a => a.id === agendaId);
        if (!item) return;

        const newResource = {
            id: Date.now(),
            type,
            title: title || (type === 'FILE' ? 'Untitled File' : 'Untitled Link'),
            url
        };

        updateAgendaItem(agendaId, 'resources', [...(item.resources || []), newResource]);
    };

    const handleFileSelect = async (agendaId, e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true); // Start loading state
        
        try {
            // Define path: e.g., pb-books/{bookId}/meeting-resources
            const path = `pb-books/${bookId}/meeting-resources`;
            
            // Call the provided asynchronous upload service
            const uploadResult = await firestoreService.uploadFile(file, path); 

            // Use the returned URL and name to create the resource item
            addResource(agendaId, 'FILE', uploadResult.fileName, uploadResult.url);
            
            // Clear the file input display value immediately
            if (e.target) e.target.value = '';

        } catch (error) {
            console.error('Failed to upload meeting resource:', error);
            alert(`File upload failed: ${error.message || 'Check console for details.'}`);
        } finally {
            setIsUploading(false); // End loading state
        }
    };

    const deleteResource = (agendaId, resourceId) => { // <--- DEFINITION ADDED
        const item = (meeting.agenda || []).find(a => a.id === agendaId);
        if (!item || !item.resources) return;
        
        // Update the specific agenda item by filtering out the resourceId
        updateAgendaItem(agendaId, 'resources', item.resources.filter(r => r.id !== resourceId));
    };
    
    const handleAddResourceLink = (agendaId) => {
        if (!newResourceLink) return;
        const item = (meeting.agenda || []).find(a => a.id === agendaId);
        if (!item) return;

        const newResource = {
            id: Date.now(),
            type: 'LINK',
            title: newResourceTitle || newResourceLink,
            url: newResourceLink
        };
        updateAgendaItem(agendaId, 'resources', [...(item.resources || []), newResource]);
        setNewResourceLink('');
        setNewResourceTitle('');
    };

    const deleteAgendaItem = (id) => {
        // Filter the current agenda items, keeping only those whose ID does NOT match the one to delete
        const newAgenda = (meeting.agenda || []).filter(item => item.id !== id);
        handleUpdate('agenda', newAgenda);
    };

    const addParticipant = () => {
        if (!newParticipant.trim()) return;
        const p = {
            id: Date.now(),
            name: newParticipant,
            email: '',
            present: false
        };
        handleUpdate('participants', [...(meeting.participants || []), p]);
        setNewParticipant('');
    };

    // --- MINUTES LOGIC (Restored with Task details) ---
    const addMinuteEntry = () => {
        if (!minuteInput.trim()) return;

        const entry = {
            id: Date.now(),
            type: minuteType,
            content: minuteInput,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            agendaItemId: selectedAgendaItem, // <-- CRITICAL: Link to Agenda Item ID
            assigneeId: minuteType === MinuteType.TASK ? taskOwner : undefined,
            dueDate: minuteType === MinuteType.TASK ? taskDeadline : undefined,
            completed: false,
        };

        handleUpdate('minutes', [...(meeting.minutes || []), entry]);
        setMinuteInput('');
        setTaskOwner('');
        setTaskDeadline('');
        setSelectedAgendaItem(''); 
        setMinuteType(MinuteType.NOTE);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingDuration(0);

            timerRef.current = window.setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error("Error accessing microphone:", err);
            alert("Could not access microphone. Please allow permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            setIsRecording(false);
            
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    const handleVoiceNoteSave = async ({ blob, duration, speaker }) => {
        // Step 1: Upload the audio blob persistently
        if (!firestoreService?.uploadFile) {
            alert("File upload service is required to save the voice note.");
            return;
        }

        setIsTranscribing(true); // Use this state for the upload/transcription duration

        try {
            const uploadResult = await firestoreService.uploadFile(blob, `pb-books/${bookId}/meeting-voice-notes`, {
                contentType: blob.type
            });

            const voiceNoteData = {
                url: uploadResult.url,
                duration: duration,
                mimeType: uploadResult.fileType,
                speaker: speaker,
                uploadedAt: new Date().toISOString()
            };
            
            // Step 2: Attempt Transcription (Optional, but useful)
            const transcriptionResult = await transcribeAudioCF({
                audioUrl: voiceNoteData.url,
                mimeType: voiceNoteData.mimeType
            });
            
            // Step 3: Add to Minutes (as a note with attachment)
            const content = transcriptionResult.data?.transcript || `[Voice Note: ${speaker.name} spoke for ${formatTime(duration)}]. Transcription failed or is unavailable.`;

            const entry = {
                id: Date.now(),
                type: MinuteType.NOTE,
                content: content,
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                agendaItemId: '',
                audioAttachment: voiceNoteData, // Store the uploaded file data
            };
            
            handleUpdate('minutes', [...(meeting.minutes || []), entry]);
            setIsRecorderOpen(false);
        } catch (error) {
            console.error('Failed to process voice note:', error);
            alert(`Failed to save/transcribe voice note: ${error.message}`);
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleTranscribeRecording = async () => {
        if (audioChunksRef.current.length === 0) return;
        
        setIsTranscribing(true);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // NOTE: This assumes 'aiService' is accessible for transcription, 
        // similar to how the original component (DiaryMeetingEditor.jsx) used it.
        
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
            try {
                // Placeholder for actual AI transcription service call
                const transcript = "[Transcription: AI Service Not Configured in Tool, Placeholder Text]";
                // const transcript = await aiService.transcribeAudio(base64Audio, 'audio/webm'); // Original logic assumption
                
                setMinuteInput(prev => (prev ? prev + '\n\n' : '') + `${transcript}`);
                setRecordingDuration(0);
                audioChunksRef.current = [];
            } catch (error) {
                console.error(error);
                alert("Transcription failed. Please check AI service configuration.");
            } finally {
                setIsTranscribing(false);
            }
        };
    };

    const toggleTaskCompletion = (id) => {
        const newMinutes = (meeting.minutes || []).map(m => 
            m.id === id ? { ...m, completed: !m.completed } : m
        );
        handleUpdate('minutes', newMinutes);
    };


    const startMeeting = () => {
        handleUpdate('status', MeetingStatus.ACTIVE);
        setActiveTab('run');
    };

    const endMeeting = () => {
        handleUpdate('status', MeetingStatus.COMPLETED);
        setActiveTab('review');
    };
    
    // Helper to find participant name
    const getParticipantName = (id) => (meeting.participants || []).find(p => p.id === id)?.name || 'Unassigned';

    // RENDER LOGIC
    return (
        <div className="h-full flex-1 overflow-y-auto flex-col bg-gray-50 overflow-hidden relative">
            
            {/* Header (Same as simplified version) */}
            <div className="bg-white border-b px-2 py-4 flex items-center justify-between shrink-0">
                {/* ... (Header content unchanged) ... */}
                <div className="flex items-center space-x-2">
                    <button onClick={onBack} className="hover:bg-gray-100 rounded-xl">
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-gray-900">{meeting.title || 'Untitled Meeting'}</h1>          
                        <div className="flex items-center text-sm text-gray-500 mt-1 space-x-4">
                            <span className="flex items-center"><Calendar className="w-4 h-4 mr-1"/> {meeting.date}</span>
                            <Badge color={getStatusStyles(meeting.status).color}>{meeting.status}</Badge>
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {meeting.status === MeetingStatus.PLANNED && (
                         <button onClick={startMeeting} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2">
                           <Play className="w-4 h-4" /> 
                           <span className="hidden sm:inline">Start Meeting</span>
                         </button>
                    )}
                    {meeting.status === MeetingStatus.ACTIVE && (
                         <button onClick={endMeeting} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2">
                           <CheckSquare className="w-4 h-4" /> 
                           <span className="hidden sm:inline">End Meeting</span>
                         </button>
                    )}
                    <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        <span className="hidden sm:inline">Save</span>
                    </button>
                </div>
            </div>
            
            {/* Tabs (Same as simplified version) */}
            <div className="bg-white border-b px-2 flex space-x-6 text-sm font-medium shrink-0">
                 {['plan', 'run', 'review'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} 
                        className={`py-3 border-b-2 px-2 transition-colors ${activeTab === tab ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                 ))}
            </div>
            
            {/* Content */}
                <div className="max-w-4xl mx-auto space-y-6">

                    {/* --- PLAN TAB (FULL) --- */}
                    {activeTab === 'plan' && (
                        <div className="space-y-4">
                            {/* Meeting Details Card */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-2 sm:p-6 space-y-4">
                                <h2 className="font-semibold text-gray-900">Meeting Details & Objectives</h2>
                                <input placeholder="Title" value={meeting.title} onChange={(e) => handleUpdate('title', e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />
                                <div className="grid grid-cols-2 gap-4">
                                    <input type="date" value={meeting.date} onChange={(e) => handleUpdate('date', e.target.value)} className="px-4 py-2.5 text-sm border border-gray-200 rounded-xl" />
                                    <input type="time" value={meeting.time} onChange={(e) => handleUpdate('time', e.target.value)} className="px-4 py-2.5 text-sm border border-gray-200 rounded-xl" />
                                </div>
                                <input placeholder="Location or Link" value={meeting.location} onChange={(e) => handleUpdate('location', e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl" />

                                {/* Objectives Section */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Objectives</label>
                                    <div className="flex space-x-2 mb-3">
                                        <input placeholder="Add a key objective..." value={newObjective} onChange={(e) => setNewObjective(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addObjective()} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl" />
                                        <button onClick={addObjective} className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl"><Plus className="w-5 h-5" /></button>
                                    </div>
                                    <div className="space-y-2">
                                        {(meeting.objectives || []).map((obj, index) => (
                                            <div key={index} className="flex items-center justify-between bg-indigo-50 p-2.5 rounded-md border border-indigo-100">
                                                <span className="text-gray-700 text-sm flex items-start"><Target className="w-4 h-4 mr-2.5 mt-0.5 text-indigo-500 shrink-0" />{obj}</span>
                                                <button onClick={() => removeObjective(index)} className="text-gray-400 hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Agenda Card */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-2 sm:p-6 space-y-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Agenda</h3>
                                    <button onClick={addAgendaItem} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2"
                                    style={{ backgroundColor: customStyling?.primaryColor || '#4F46E5' }}
                                    >
                                        <Plus className="w-4 h-4" /> Add Item</button>
                                </div>
                                
                                {(meeting.agenda || []).map((item) => (
                                    <div key={item.id} className="bg-gray-50 rounded-lg border border-gray-100 mb-3">
                                        <div className="flex items-center space-x-3 p-1 cursor-pointer" onClick={() => togglePlanAgendaExpand(item.id)}>
                                            <span className="text-gray-400 hover:text-gray-600 transition-colors">
                                                {expandedPlanAgendaId === item.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                            </span>
                                            <input className="bg-transparent font-medium border border-gray-300 rounded-lg text-gray-800 w-full outline-none" value={item.title} onChange={(e) => updateAgendaItem(item.id, 'title', e.target.value)} placeholder="Item title" />

                                            <div className="w-24">
                                                <input 
                                                    type="number" 
                                                    className="bg-transparent border border-gray-300 rounded-lg  text-sm text-gray-600 w-full text-right" 
                                                    value={item.durationMinutes} 
                                                    // --- ADD THE CORRECT onChange HANDLER HERE ---
                                                    onChange={(e) => updateAgendaItem(item.id, 'durationMinutes', parseInt(e.target.value) || 0)} 
                                                    // ---------------------------------------------
                                                />
                                            </div>
                                            <span className="text-xs text-gray-400">min</span>
                                            <button 
                                                // This is the (+) button logic for incrementing
                                                onClick={(e) => { e.stopPropagation(); updateAgendaItem(item.id, 'durationMinutes', (item.durationMinutes || 0) + 5); }} 
                                                className="text-gray-400 sm:hidden hover:text-indigo-600"
                                            >
                                                +
                                            </button>
                                            <button 
                                                // --- ADD A DEDICATED MINUS BUTTON ---
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    updateAgendaItem(item.id, 'durationMinutes', Math.max(0, (item.durationMinutes || 0) - 5)); 
                                                }} 
                                                className="text-gray-400 hover:text-red-600"
                                            >
                                                −
                                            </button>

                                            {/* DELETE BUTTON (CRITICAL ADDITION) */}
                                            <button 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (window.confirm(`Delete agenda item "${item.title}"?`)) {
                                                        deleteAgendaItem(item.id); 
                                                    }
                                                }} 
                                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                                                title="Delete Item"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {expandedPlanAgendaId === item.id && (
                                            <div className="p-3 bg-white border-t border-gray-100 rounded-b-md space-y-3">
                                                <input placeholder="Presenter" value={item.presenter} onChange={(e) => updateAgendaItem(item.id, 'presenter', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                                                
                                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center"><FileText className="w-3 h-3 mr-1.5" /> Resources</h4>
                                                
                                                {/* --- RESTORED RESOURCE DISPLAY MAP --- */}
                                                <div className="space-y-2 mb-4">
                                                    {(!item.resources || item.resources.length === 0) && <p className="text-xs text-gray-400 italic">No resources attached.</p>}
                                                    
                                                    {(item.resources || []).map(resource => (
                                                    <div key={resource.id} className="flex items-center justify-between p-2 bg-indigo-50 rounded border border-indigo-100 group">
                                                        <div className="flex items-center overflow-hidden">
                                                        {resource.type === 'LINK' ? <LinkIcon className="w-3 h-3 mr-2 text-indigo-500 shrink-0" /> : <FileText className="w-3 h-3 mr-2 text-indigo-500 shrink-0" />}
                                                        <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-700 hover:underline truncate">{resource.title}</a>
                                                        </div>
                                                        <button 
                                                            onClick={() => deleteResource(item.id, resource.id)} 
                                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                    ))}
                                                </div>
                                                {/* --- END RESTORED RESOURCE DISPLAY MAP --- */}
                                                
                                                {/* --- RESOURCE INPUTS (WITH UPLOAD LOGIC) --- */}
                                                <div className="flex flex-col sm:flex-row gap-2 items-end sm:items-center bg-gray-50 p-2 rounded">
                                                    {/* ... (input fields and buttons for link/upload logic follow this point) ... */}
                                                    <div className="flex-1 w-full space-y-2 sm:space-y-0 sm:flex sm:gap-2">
                                                        <input placeholder="Resource Title (e.g. Q3 Strategy)" className="text-xs p-2 rounded border border-gray-300 w-full outline-none focus:border-indigo-500" value={newResourceTitle} onChange={(e) => setNewResourceTitle(e.target.value)} />
                                                        <input placeholder="https://..." className="text-xs p-2 rounded border border-gray-300 w-full outline-none focus:border-indigo-500" value={newResourceLink} onChange={(e) => setNewResourceLink(e.target.value)} />
                                                    </div>
                                                    <div className="flex gap-1 shrink-0">
                                                        <button 
                                                            onClick={() => handleAddResourceLink(item.id)} 
                                                            disabled={!newResourceLink || isUploading} 
                                                            className="px-3 py-1.5 h-[34px] text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                                        >
                                                            Add Link
                                                        </button>
                                                        
                                                        {/* FILE UPLOAD OPTION (Assuming isUploading state and handlers are defined) */}
                                                        <div className="relative">
                                                            <input 
                                                                type="file" 
                                                                className="hidden" 
                                                                id={`file-upload-${item.id}`}
                                                                onChange={(e) => handleFileSelect(item.id, e)} 
                                                                disabled={isUploading} 
                                                            />
                                                            <label 
                                                                htmlFor={`file-upload-${item.id}`}
                                                                className={`flex items-center justify-center px-3 py-1.5 h-[34px] text-xs rounded-lg cursor-pointer transition-colors ${isUploading 
                                                                    ? 'bg-gray-300 text-gray-600' 
                                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                                            >
                                                                {isUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : 'Upload File'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            
                            {/* Participants Card (Same as simplified version) */}
                            <div className="bg-white rounded-xl border border-gray-100 p-2 sm:p-6">
                                <h3 className="font-semibold text-gray-800 mb-4">Participants</h3>
                                <div className="flex space-x-2 mb-4">
                                    <input placeholder="Enter name" value={newParticipant} onChange={(e) => setNewParticipant(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addParticipant()} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl" />
                                    <button onClick={addParticipant} className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl"><Plus className="w-4 h-4" /></button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(meeting.participants || []).map(p => (
                                        <Badge key={p.id} color="indigo">{p.name}</Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* RUN TAB */}
                    {activeTab === 'run' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* === LEFT COLUMN: RECORDER & AGENDA STATUS === */}
                            <div className="md:col-span-1 space-y-6">

                                {/* --- INTEGRATED VOICE RECORDER PANEL --- */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                                    <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Mic className="w-5 h-5 text-purple-600" /> Record Discussion</h2>
                                    
                                    {isRecorderOpen ? (
                                        <VoiceRecorder // <-- Use the shared component
                                            context="meeting"
                                            showSpeakerInfo={true}
                                            onSave={handleVoiceNoteSave} // Must be defined and pass blob to upload service
                                            onCancel={() => setIsRecorderOpen(false)}
                                            maxDuration={300} // 5 minutes
                                        />
                                    ) : (
                                        <button 
                                            onClick={() => setIsRecorderOpen(true)}
                                            className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 flex items-center justify-center gap-2 font-medium"
                                            disabled={isTranscribing}
                                        >
                                            <Mic className="w-5 h-5" /> Start Voice Note
                                        </button>
                                    )}
                                    {isTranscribing && <p className="text-sm text-purple-600 mt-2 text-center flex items-center justify-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading and Transcribing...</p>}
                                </div>

                                {/* Agenda Status Card (Using existing logic/structure) */}
                                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Agenda Status</h3>
                                    <div className="space-y-3">
                                        {/* Placeholder for Agenda status items, using existing item.id logic */}
                                        {(meeting.agenda || []).map(item => (
                                            <div key={item.id} className="text-sm p-2 rounded border border-gray-100 hover:border-indigo-100 flex items-center justify-between">
                                                <span className={`truncate ${item.completed ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                                                    {item.title}
                                                </span>
                                                <input 
                                                    type="checkbox" 
                                                    checked={item.completed} 
                                                    onChange={(e) => updateAgendaItem(item.id, 'completed', e.target.checked)} 
                                                    className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500" 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* === CENTER COLUMN: MINUTES CAPTURE === */}
                            <div className="md:col-span-2 space-y-6">
                                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                                    <h2 className="font-semibold text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-indigo-600" /> Capture Minutes</h2>
                                    
                                    {/* Agenda Item Selector */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Agenda Item *</label>
                                        <select
                                            value={selectedAgendaItem}
                                            onChange={(e) => setSelectedAgendaItem(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white"
                                        >
                                            <option value="">(Global Note - No specific item)</option>
                                            {(meeting.agenda || []).map(item => (
                                                <option key={item.id} value={item.id}>{item.title} ({item.durationMinutes}m)</option>
                                            ))}
                                        </select>
                                    </div>

                                    <textarea rows={4} placeholder="Type notes, decisions, or action items..." value={minuteInput} onChange={(e) => setMinuteInput(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none" />
                                    
                                    <div className="flex flex-wrap items-center gap-3">
                                        {/* Minute Type Buttons (from previous implementation) */}
                                        <button onClick={() => setMinuteType('NOTE')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${minuteType === 'NOTE' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 border-gray-200'}`}>Note</button>
                                        <button onClick={() => setMinuteType('DECISION')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${minuteType === 'DECISION' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border-gray-200'}`}>Decision</button>
                                        <button onClick={() => setMinuteType('TASK')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${minuteType === 'TASK' ? 'bg-amber-600 text-white' : 'bg-white text-gray-600 border-gray-200'}`}>Action Item</button>

                                        {/* Task Details (if minuteType is TASK) */}
                                        {minuteType === 'TASK' && (
                                            <div className="flex gap-4 p-3 bg-amber-50 rounded-md border border-amber-100">
                                                {/* ... (Task Owner/Deadline Selects) ... */}
                                            </div>
                                        )}

                                        <button onClick={addMinuteEntry} disabled={!minuteInput.trim()} className="ml-auto px-4 py-1.5 bg-indigo-600 text-white rounded-xl">Add Entry</button>
                                    </div>
                                </div>

                                {/* Live Feed */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-gray-500 uppercase">Live Feed</h4>
                                    {([...(meeting.minutes || [])].reverse()).map(entry => {
                                        const agendaItem = (meeting.agenda || []).find(item => item.id === entry.agendaItemId);
                                        
                                        return (
                                            <div key={entry.id} className={`p-3 rounded-lg border ${entry.type === MinuteType.DECISION ? 'bg-blue-50 border-blue-100' : entry.type === MinuteType.TASK ? 'bg-amber-50 border-amber-100' : 'bg-white border-gray-100'}`}>
                                                
                                                {/* AGENDA ITEM HEADING (NEW REQUIREMENT) */}
                                                {agendaItem && (
                                                    <p className="text-[10px] font-bold text-gray-500 mb-1 truncate">
                                                        {agendaItem.title}
                                                    </p>
                                                )}
                                                
                                                <div className="flex justify-between items-start">
                                                    <span className={`text-[10px] font-bold uppercase tracking-wide ${entry.type === MinuteType.TASK ? 'text-amber-800' : 'text-gray-500'}`}>{entry.type}</span>
                                                    <span className="text-[10px] text-gray-400">{entry.timestamp}</span>
                                                </div>
                                                <p className="text-sm mt-1 text-gray-800 line-clamp-2">{entry.content}</p>
                                                
                                                {/* VOICE PLAYER INTEGRATION (Show if audio is attached) */}
                                                {entry.audioAttachment && (
                                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                                        {/* Placeholder integration for VoicePlayer */}
                                                        <VoicePlayer voice={entry.audioAttachment} title="Voice Note" position="center" autoPlay={false} onClose={() => { /* handle player close if needed */ }} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- REVIEW TAB (FULL) --- */}
                    {activeTab === 'review' && (
                        <div className="space-y-6">
                            {/* Action Items (Restored with toggle) */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-6">
                                <h2 className="font-semibold text-gray-900 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-amber-600" /> Action Items</h2>
                                <ul className="space-y-3 pt-3">
                                    {(meeting.minutes || []).filter(m => m.type === MinuteType.TASK).map(m => (
                                        <li key={m.id} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                                            <div className="pt-0.5">
                                                <input type="checkbox" checked={!!m.completed} onChange={() => toggleTaskCompletion(m.id)} className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer" />
                                            </div>
                                            <div className="flex-1">
                                                <div className={`text-sm font-medium ${m.completed ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{m.content}</div>
                                                <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                                                    <span>Owner: {getParticipantName(m.assigneeId)}</span>
                                                    {m.dueDate && <span>Due: {m.dueDate}</span>}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                    {(meeting.minutes || []).filter(m => m.type === MinuteType.TASK).length === 0 && <p className="text-gray-400 text-sm">No action items recorded.</p>}
                                </ul>
                            </div>

                            {/* Agenda Summary (Restored) */}
                             <div className="bg-white rounded-2xl border border-gray-100 p-6">
                                 <h2 className="font-semibold text-gray-900 mb-4 flex items-center"><List className="w-5 h-5 mr-2 text-indigo-600" />Agenda Summary & Outcomes</h2>
                                 <div className="space-y-4 divide-y divide-gray-100">
                                     {(meeting.agenda || []).map(item => (
                                        <div key={item.id} className="pt-4 first:pt-0">
                                            <h4 className="font-medium text-gray-800">{item.title} ({item.durationMinutes}m)</h4>
                                            {(item.notes || item.decisions) ? (
                                                <div className="space-y-2 bg-gray-50 p-3 rounded-md text-sm mt-2">
                                                   {item.notes && (<div><span className="font-semibold text-gray-600 text-xs uppercase tracking-wide">Discussion Notes:</span><p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{item.notes}</p></div>)}
                                                   {item.decisions && (<div className={item.notes ? 'mt-3 pt-3 border-t border-gray-200' : ''}><span className="font-semibold text-blue-600 text-xs uppercase tracking-wide">Decision:</span><p className="text-blue-900 mt-0.5 font-medium whitespace-pre-wrap">{item.decisions}</p></div>)}
                                                </div>
                                            ) : (<p className="text-xs text-gray-400 italic">No notes or decisions recorded for this item.</p>)}
                                        </div>
                                     ))}
                                 </div>
                             </div>
                        </div>
                    )}
                </div>
        </div>
    );
};


// ============================================================
// MAIN COMPONENT (MeetingTool)
// ============================================================

const MeetingTool = ({ bookId, customStyling = {}, toolsService, onBack }) => {
    const toolSlug = 'meetings';
    const templateSlug = 'meeting-templates';
    
    // State
    const [viewState, setViewState] = useState(ViewState.DASHBOARD);
    const [meetings, setMeetings] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);

    // Load data
    useEffect(() => {
        loadData();
    }, [bookId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [meetingsData, templatesData] = await Promise.all([
                toolsService.getToolData(bookId, toolSlug),
                toolsService.getToolData(bookId, templateSlug),
            ]);
            
            setMeetings(meetingsData || []);
            setTemplates(templatesData || []);
        } catch (error) {
            console.error('Error loading meeting data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate stats
    const stats = useMemo(() => {
        const planned = meetings.filter(m => m.status === MeetingStatus.PLANNED).length;
        const completed = meetings.filter(m => m.status === MeetingStatus.COMPLETED).length;
        const active = meetings.filter(m => m.status === MeetingStatus.ACTIVE).length;
        
        return {
            total: meetings.length,
            planned,
            completed,
            active
        };
    }, [meetings]);

    // Handlers
    const handleSaveMeeting = async (data) => {
        setSaving(true);
        try {
            const newMeeting = {
                ...data,
                status: data.status || MeetingStatus.PLANNED,
                date: data.date || new Date().toISOString().split('T')[0],
                time: data.time || '09:00',
                durationMinutes: data.durationMinutes || 60,
                type: data.type || 'VIRTUAL',
            };
            
            await toolsService.saveToolData(bookId, toolSlug, newMeeting);
            await loadData();
            setViewState(ViewState.LIST);
            setSelectedMeeting(null);
        } catch (error) {
            console.error('Error saving meeting:', error);
            alert('Failed to save meeting');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateNewMeeting = () => {
        setSelectedMeeting({ 
            title: 'New Meeting', 
            date: new Date().toISOString().split('T')[0], 
            time: '09:00',
            durationMinutes: 60,
            status: MeetingStatus.PLANNED,
            participants: [],
            minutes: []
        });
        setViewState(ViewState.EDITOR);
    }

    // Loading
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            </div>
        );
    }

    // Render based on view state
    switch (viewState) {
        case ViewState.EDITOR:
            return (
                <MeetingEditor
                    meeting={selectedMeeting}
                    onSave={handleSaveMeeting}
                    onBack={() => { setViewState(ViewState.LIST); setSelectedMeeting(null); }}
                    saving={saving}
                    customStyling={customStyling}
                />
            );

        case ViewState.LIST:
            return (
                <div className="h-full overflow-y-auto">
                    <div className="max-w-6xl mx-auto p-2 sm:p-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="hover:bg-gray-100 rounded-xl">
                                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                                </button>
                                <h1 className="text-2xl font-bold text-gray-900">Meetings List</h1>
                            </div>
                            <button
                                onClick={handleCreateNewMeeting}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                                style={{ backgroundColor: customStyling.primaryColor }}
                            >
                                <Plus className="w-4 h-4" /> 
                                <span className="hidden sm:inline">New Meeting</span>
                            </button>
                        </div>
                        <MeetingsList
                            meetings={meetings}
                            onSelectMeeting={(m) => { setSelectedMeeting(m); setViewState(ViewState.EDITOR); }}
                            onCreateMeeting={handleCreateNewMeeting}
                            // Note: Delete functionality omitted in list view for simplicity, should be implemented inside card menu
                            customStyling={customStyling}
                        />
                    </div>
                </div>
            );

        default: // DASHBOARD
            return (
                <div className="h-full overflow-y-auto">
                    <div className="max-w-6xl mx-auto p-2 sm:p-6 space-y-6">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <button onClick={onBack} className="hover:bg-gray-100 rounded-xl">
                                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                                </button>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Meeting Planner</h1>
                                    <p className="text-gray-500">Plan, run, and review your meetings</p>
                                </div>
                            </div>
                            <button
                                onClick={handleCreateNewMeeting}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                                style={{ backgroundColor: customStyling.primaryColor }}
                            >
                                <Plus className="w-4 h-4" /> 
                                <span className="hidden sm:inline">New Meeting</span>
                            </button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <StatCard
                                icon={Briefcase}
                                label="Total Meetings"
                                value={stats.total}
                                color="indigo"
                                onClick={() => setViewState(ViewState.LIST)}
                            />
                            <StatCard
                                icon={Clock}
                                label="Planned"
                                value={stats.planned}
                                color="amber"
                                onClick={() => setViewState(ViewState.LIST)}
                            />
                            <StatCard
                                icon={Play}
                                label="Active"
                                value={stats.active}
                                color="blue"
                                onClick={() => setViewState(ViewState.LIST)}
                            />
                            <StatCard
                                icon={CheckSquare}
                                label="Completed"
                                value={stats.completed}
                                color="emerald"
                                onClick={() => setViewState(ViewState.LIST)}
                            />
                        </div>

                        {/* Quick Actions */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                           <button onClick={handleCreateNewMeeting} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all text-left">
                              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><NewMeetingIcon className="w-5 h-5 text-indigo-600" /></div>
                              <span className="font-medium text-gray-900">New Plan</span>
                           </button>
                           <button onClick={() => setViewState(ViewState.LIST)} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all text-left">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><List className="w-5 h-5 text-blue-600" /></div>
                              <span className="font-medium text-gray-900">View All</span>
                           </button>
                           <button onClick={() => setViewState(ViewState.LIST)} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-md transition-all text-left">
                              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><LayoutTemplate className="w-5 h-5 text-emerald-600" /></div>
                              <span className="font-medium text-gray-900">Templates</span>
                           </button>
                           <button onClick={() => setViewState(ViewState.LIST)} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all text-left">
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Settings className="w-5 h-5 text-purple-600" /></div>
                              <span className="font-medium text-gray-900">Settings</span>
                           </button>
                        </div>
                        
                        {/* Recent Activity */}
                        <div className="bg-white rounded-2xl border border-gray-100 p-6">
                            <h2 className="font-semibold text-gray-900 mb-4">Recent Meetings</h2>
                            {meetings.length === 0 ? (
                                <p className="text-gray-400 text-sm text-center py-8">No recent meetings to display.</p>
                            ) : (
                                <div className="space-y-3">
                                    {meetings.slice(0, 5).map(m => (
                                        <div key={m.id} onClick={() => { setSelectedMeeting(m); setViewState(ViewState.EDITOR); }} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 cursor-pointer">
                                            <div className="flex items-center gap-3">
                                                <Calendar className="w-5 h-5 text-indigo-500" />
                                                <div>
                                                    <p className="font-medium text-gray-900">{m.title}</p>
                                                    <p className="text-sm text-gray-500">{formatDate(m.date)}</p>
                                                </div>
                                            </div>
                                            <Badge color={getStatusStyles(m.status).color}>{m.status}</Badge>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
    }
};

export default MeetingTool;